import { FieldValues, UseFormReturn } from 'react-hook-form'

import { MetaModel, MetaStatusCodes, NotificationSeverity } from '@/Shared/domain'
import { attachFormErrors } from '@/Shared/ui/form'
import { useNotification, useNotificationHandler } from '@/Shared/ui/notifications'

interface IOptions {
  notifySuccess?: boolean
  successMessage?: string
}

type MetaResponseHandler = {
  handleMetaResponse: <T extends FieldValues>(
    meta: MetaModel,
    form?: UseFormReturn<T>,
    options?: IOptions,
    errorMessage?: string
  ) => boolean
}

export const validateMetaResponse = (meta: MetaModel): boolean =>
  meta.status === MetaStatusCodes.SUCCESS
  
export const useMetaResponseHandler = (): MetaResponseHandler => {
  const { notificationHandler } = useNotificationHandler()
  const { addNotification } = useNotification()

  const handleMetaResponse = <T extends FieldValues>(
    meta: MetaModel | undefined,
    form?: UseFormReturn<T>,
    options: IOptions = {
      notifySuccess: undefined,
      successMessage: undefined,
    },
    errorMessage?: string
  ): boolean => {
    if (!meta) return false

    const { notifySuccess = true, successMessage } = options

    if (validateMetaResponse(meta)) {
      if (form) form.reset(form.getValues())
      if (notifySuccess) {
        successMessage
          ? addNotification(successMessage, NotificationSeverity.success)
          : notificationHandler(meta, true)
      }

      return true
    }
    else if (errorMessage) {
        addNotification(errorMessage, NotificationSeverity.error)
        return false
    }

    const attachableFormErrors = form ? attachFormErrors(form, meta.messages) : false

    if (!attachableFormErrors) {
      notificationHandler(meta)
    }

    return false
  }

  return { handleMetaResponse }
}
